import { useCallback, useRef, useState } from 'react';
import { Card, CardContent } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { Form, Formik } from 'formik';
import SimpleReactLightbox from 'simple-react-lightbox';
import { motion } from 'framer-motion';

// local imports
import { CampaignService } from 'app/services';
import { cancelRequests } from 'app/utils/axios';
import { useStateCallback } from 'app/hooks';
import { useCampaignDetailService } from 'app/hooks/services';
import { EnvDefaults, FormatUtils, ValidationSchema } from 'app/utils';
import CampaignCard from './campaign-detail-card';
import { CampaignCrudForm } from 'app/components/organisms/forms';
import { Loader, NoDataTableComponent } from 'app/components/atoms';

const CampaignEdit = ({ campaignId }) => {
  const history = useHistory();
  const formikRef = useRef();
  const isEdit = !!campaignId;
  const [loading, setLoading] = useStateCallback(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Fetch campaign detail data first;
  const { service, handleRefresh } = useCampaignDetailService(campaignId);

  // Stop upload photo and cancel current request
  const removeFile = () => cancelRequests('Request Cancelled!');

  // Submit Update Form
  const handleSubmit = useCallback(
    async values => {
      const { start_date, end_date, content, ...rest } = values;
      const valueToUse = {
        ...rest,
        ...(content ? { content } : {}),
        start_date: FormatUtils.convertPostDate(start_date),
        end_date: FormatUtils.convertPostDate(end_date)
      };

      setLoading(true, async () => {
        const serviceFn = () => CampaignService.updateCampaign(campaignId, valueToUse);
        try {
          await serviceFn();
          // re-fetch campaign detail
          handleRefresh();
          // remove photo
          setSelectedFiles([]);
          setTimeout(() => {
            history.push('/campaign-management');
          }, 2000);
        } catch (err) {
          console.log('Error -> ', err);
        } finally {
          setLoading(false);
        }
      });
    },
    [setLoading, campaignId, history, handleRefresh]
  );

  // variables - states
  const isDataLoading = !service.data && (!service.isInitialized || service.isLoading);
  const isDataExist = !!service.data && !service.isNetworkError && !service.isError;
  const { id, created_at, campaign_code, status, content_url, ...rest } =
    !!service.data && service?.data;
  const initialVal = isEdit ? { content: '', ...rest } : EnvDefaults.addEditCampaignDefaults;

  return (
    <>
      <SimpleReactLightbox>
        <Loader loading={isDataLoading} />
        {isDataExist ? (
          <>
            <motion.div initial={{ scale: 0, opacity: 0.2 }} animate={{ scale: 1, opacity: 1 }}>
              <CampaignCard handleRefresh={handleRefresh} service={service?.data} />
              <Card elevation={4}>
                <CardContent>
                  <Formik
                    innerRef={formikRef}
                    enableReinitialize
                    initialValues={initialVal}
                    validationSchema={ValidationSchema.campaignCrudSchema(true)}
                    onSubmit={handleSubmit}>
                    <Form>
                      <CampaignCrudForm
                        isEdit
                        loading={loading}
                        selectedFiles={selectedFiles}
                        setSelectedFiles={setSelectedFiles}
                        removeFile={removeFile}
                      />
                    </Form>
                  </Formik>
                </CardContent>
              </Card>
            </motion.div>
          </>
        ) : (
          !isDataLoading && <NoDataTableComponent />
        )}
      </SimpleReactLightbox>
    </>
  );
};

export default CampaignEdit;
